import { useCallback, useEffect, useRef, useState } from "react";
import { fetchProfiles } from "../../../../../api/profile";
import {
    getUserCategoryForBackendType,
    mapUserTypeBackendToDisplay,
    mapUserTypeDisplayToBackend,
} from "../../../../../utils/userRoleUtils";

export default function useUsersTabData({ rowsPerPage, sortConfig }) {
  const [realData, setRealData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [activeDateFilter, setActiveDateFilter] = useState(null);
  const [activeLoginFilter, setActiveLoginFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);

  const fetchRealDataRef = useRef();

  const transformProfileToUser = useCallback((profile) => {
    const displayUserType = mapUserTypeBackendToDisplay(profile.type);

    const formatDateYYYYMMDD = (dateString) => {
      if (!dateString) {
        return "-";
      }
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const formatDateWithTime = (dateString) => {
      if (!dateString) return "-";

      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const timeString = `${hours}:${minutes} ${ampm}`;

      return `${year}-${month}-${day} ${timeString}`;
    };

    const getDateColor = (dateString) => {
      if (!dateString) return "#6b7280";

      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 30) {
        return "#16a34a";
      } else if (diffDays <= 60) {
        return "#ea580c";
      }
      return "#dc2626";
    };

    const category =
      getUserCategoryForBackendType(profile.type) ||
      (profile.created_by_id ? "Internal" : "External");

    return {
      id: profile.id,
      name:
        profile.user?.first_name || profile.user?.last_name
          ? `${profile.user.first_name || ""} ${profile.user.last_name || ""}`.trim()
          : profile.user?.email || "Incomplete Profile",
      email: profile.user?.email || "Incomplete Profile",
      createdDate: formatDateYYYYMMDD(profile.created_on),
      createdOnTimestamp: profile.created_on,
      latestLogin: formatDateWithTime(profile.modified_on),
      latestLoginTimestamp: profile.modified_on,
      latestLoginColor: getDateColor(profile.modified_on),
      userType: displayUserType,
      createdBy: category,
      createdByType: category.toLowerCase(),
      accounts: profile.accounts_count || 0,
      accountNames: null,
      originalProfile: profile,
      isActive: profile.is_active,
      inviteStatus: profile.invite_status,
    };
  }, []);

  const fetchRealData = useCallback(
    async (cursor = null, search = "", isNewSearch = false, filters = {}, dateFilter = null) => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          page_size: rowsPerPage,
          legacy_user: false,
        };

        if (cursor) {
          params.cursor = cursor;
        }

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (filters.name && filters.name.trim()) {
          params.name = filters.name.trim();
        }
        if (filters.email && filters.email.trim()) {
          params.email = filters.email.trim();
        }

        if (dateFilter) {
          if (dateFilter.customStartDate && dateFilter.customEndDate) {
            params.created_after = dateFilter.customStartDate;
            params.created_before = dateFilter.customEndDate;
          } else if (dateFilter.dateRange) {
            const now = new Date();
            let startDate;

            switch (dateFilter.dateRange) {
              case "24h":
                startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                break;
              case "7d":
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
              case "30d":
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
              default:
                startDate = null;
            }

            if (startDate) {
              params.created_after = startDate.toISOString().split("T")[0];
            }
          }
        }

        if (filters.userType && filters.userType.trim()) {
          const selectedType = filters.userType.trim();

          if (selectedType === "Bookkeeper") {
            params.bookkeeper_type = "true";
          }

          const backendUserType = mapUserTypeDisplayToBackend(selectedType);
          if (backendUserType && selectedType !== "Bookkeeper") {
            params.type = backendUserType;
          }
        }

        if (filters.createdBy && filters.createdBy.trim()) {
          params.category = filters.createdBy.trim();
        }

        if (sortConfig && sortConfig.field && sortConfig.direction) {
          const fieldMap = {
            name: "full_name",
            email: "user__email",
            createdDate: "created_on",
            latestLogin: "modified_on",
            userType: "type",
          };

          const backendField = fieldMap[sortConfig.field] || sortConfig.field;
          const prefix = sortConfig.direction === "desc" ? "-" : "";
          params.ordering = `${prefix}${backendField}`;
        }

        const response = await fetchProfiles(params);
        const transformedData = (response.results || []).map(transformProfileToUser);

        setRealData(transformedData);
        setNextCursor(response.next ? new URL(response.next).searchParams.get("cursor") : null);
        setPrevCursor(response.previous ? new URL(response.previous).searchParams.get("cursor") : null);
      } catch (err) {
        setError("Failed to load users data");
      } finally {
        setLoading(false);
      }
    },
    [rowsPerPage, sortConfig, transformProfileToUser]
  );

  fetchRealDataRef.current = fetchRealData;

  const handleNextPage = useCallback(() => {
    if (nextCursor) {
      setPage((prev) => prev + 1);
      fetchRealDataRef.current(nextCursor, searchTerm, false, activeFilters, activeDateFilter);
    }
  }, [nextCursor, searchTerm, activeFilters, activeDateFilter]);

  const handlePreviousPage = useCallback(() => {
    if (prevCursor) {
      setPage((prev) => prev - 1);
      fetchRealDataRef.current(prevCursor, searchTerm, false, activeFilters, activeDateFilter);
    } else {
      setPage(0);
      fetchRealDataRef.current(null, searchTerm, false, activeFilters, activeDateFilter);
    }
  }, [prevCursor, searchTerm, activeFilters, activeDateFilter]);

  const handleSearchChange = useCallback(
    (searchValue) => {
      if (searchValue === searchTerm) {
        return;
      }

      setSearchTerm(searchValue);
      setPage(0);
      fetchRealDataRef.current(null, searchValue, true, activeFilters, activeDateFilter);
    },
    [searchTerm, activeFilters, activeDateFilter]
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      setActiveFilters(newFilters);
      setPage(0);
      fetchRealDataRef.current(null, searchTerm, true, newFilters, activeDateFilter);
    },
    [searchTerm, activeDateFilter]
  );

  const handleDateFilterChange = useCallback(
    (dateFilter) => {
      setActiveDateFilter(dateFilter);
      setPage(0);
      fetchRealDataRef.current(null, searchTerm, true, activeFilters, dateFilter);
    },
    [searchTerm, activeFilters]
  );

  const handleLoginFilterChange = useCallback((loginFilter) => {
    setActiveLoginFilter(loginFilter);
  }, []);

  const refresh = useCallback(() => {
    fetchRealDataRef.current(null, searchTerm, true, activeFilters, activeDateFilter);
  }, [searchTerm, activeFilters, activeDateFilter]);

  useEffect(() => {
    fetchRealDataRef.current(null, "", true, {}, null);
  }, []);

  useEffect(() => {
    setPage(0);
    fetchRealDataRef.current(null, searchTerm, true, activeFilters, activeDateFilter);
  }, [rowsPerPage, sortConfig, searchTerm, activeFilters, activeDateFilter]);

  return {
    data: realData,
    loading,
    error,
    searchTerm,
    page,
    nextCursor,
    prevCursor,
    handleNextPage,
    handlePreviousPage,
    handleSearchChange,
    handleFiltersChange,
    handleDateFilterChange,
    activeDateFilter,
    handleLoginFilterChange,
    activeLoginFilter,
    refresh,
  };
}
